import { Stripe, StripeElements } from '@stripe/stripe-js';
import { match, P } from 'ts-pattern';
import env from '~/env';

type State = {
  secret: string | null;
  stripeInstance: Stripe | null;
  paymentUI: ReturnType<typeof createPaymentUI> | null;
};

const state: State = {
  secret: null,
  stripeInstance: null,
  paymentUI: null,
};
const resetState = () => {
  state.secret = null;
  state.stripeInstance = null;
  state.paymentUI = null;
};

type ListenerFunction = (state: State) => void;
const listeners: Set<ListenerFunction> = new Set();
const notifyChange = () => listeners.forEach((l) => l(state));

const createPaymentUI = (elements: StripeElements) => ({
  getCardElement() {
    const card = elements.getElement('card');

    if (!card) {
      throw new Error(
        'Card element was not created. Ensure you have called `mount` before accessing the card element.',
      );
    }

    return card;
  },

  mount: (domId: string) => {
    const cardElement = elements.create('card');

    cardElement.mount(domId);

    return () => {
      cardElement.unmount();
      cardElement.destroy();
    };
  },
});

const createStripe = (stripeAccount: string) => {
  if (!window.Stripe) {
    throw new Error('Stripe has not been loaded');
  }

  return window.Stripe(String(env.VITE_STRIPE_PUBLISHABLE_KEY), {
    stripeAccount,
  });
};

const Payments = {
  initializePayment: (clientSecret: string, stripeIdFromPlatform: string) => {
    state.secret = clientSecret;
    state.stripeInstance = createStripe(stripeIdFromPlatform);
    state.paymentUI = createPaymentUI(
      state.stripeInstance.elements({
        clientSecret,
      }),
    );

    notifyChange();
  },

  confirmPayment: async () => {
    if (!state.secret || !state.stripeInstance || !state.paymentUI) {
      throw new Error('Payment has not been created', {
        cause: {
          secret: !!state.secret,
          stripe: !!state.stripeInstance,
          paymentUI: !!state.paymentUI,
        },
      });
    }

    const result = await state.stripeInstance.confirmCardPayment(state.secret, {
      payment_method: { card: state.paymentUI.getCardElement() },
    });

    return match(result)
      .with({ paymentIntent: P.nonNullable }, ({ paymentIntent }) => {
        resetState();
        notifyChange();
        return paymentIntent.id;
      })
      .with({ error: P.nonNullable }, ({ error }) => {
        throw new Error('Failed to confirm payment', { cause: error });
      })
      .exhaustive();
  },

  subscribe: (listener: ListenerFunction) => {
    listeners.add(listener);

    return () => {
      listeners.delete(listener);
    };
  },

  getSnapshot: () => state,
};

export default Payments;
